/*
  File: dashboardSchedule.ts
  Path: /Users/larrymclean/CascadeProjects/aquorix-frontend/src/api/dashboardSchedule.ts
  Description:
    API client for operator dashboard schedule.
    - Injects Supabase Bearer token
    - Supports read, create, update, cancel, delete
    - Supports weekly schedule pattern operations
    - Deterministic error normalization (403/409)

  Author: AQUORIX Team
  Created: 2026-02-15
  Version: 1.3.0

  Change Log:
    - 2026-02-16 - v1.1.0:
      - Added createDashboardSession()
      - Added cancelDashboardSession()
    - 2026-05-15 - v1.2.0:
      - Added updateDashboardSession() client for existing PATCH schedule session route.
    - 2026-06-01 - v1.3.0:
      - Added getDashboardCalendar() client for range-based Calendar inventory.
*/

import { supabase } from "../lib/supabaseClient";

import API_BASE_URL from "../utils/api";

const DASHBOARD_BASE = `${API_BASE_URL}/api/v1/dashboard`;

async function getAccessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Not authenticated (no Supabase session token)");
  return token;
}

function mapBackendStatusToHttp(status: any): number | undefined {
  if (status === 403 || status === 409) return status;
  if (status === "forbidden") return 403;
  if (status === "conflict") return 409;
  return undefined;
}

function throwApiError(json: any, fallbackMessage: string, fallbackStatus?: number): never {
  const err: any = new Error(json?.message || fallbackMessage);
  err.status =
    mapBackendStatusToHttp(json?.status) ??
    fallbackStatus ??
    500;
  err.raw = json;
  throw err;
}

export async function getDashboardSchedule(weekStart?: string) {
  const token = await getAccessToken();

  const url = new URL(`${DASHBOARD_BASE}/schedule`);
  if (weekStart && weekStart.trim().length > 0) {
    url.searchParams.set("week_start", weekStart.trim());
  }

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Schedule fetch failed", res.status);
  }

  return json;
}

export async function getDashboardCalendar(start: string, end: string) {
  const token = await getAccessToken();

  const url = new URL(`${DASHBOARD_BASE}/calendar`);
  url.searchParams.set("start", start.trim());
  url.searchParams.set("end", end.trim());

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Calendar fetch failed", res.status);
  }

  return json;
}

export async function createDashboardSession(payload: {
  itinerary_id?: number;
  team_id?: number;
  dive_site_id: number;
  dive_datetime: string;
  meet_time?: string;
  meet_location?: string;
  notes?: string;
  session_type?: string;
  dive_category?: string | null;
  vessel_id?: number;
  lead_guide_id?: number | null;
  price_per_diver?: string | number | null;
}) {
  const token = await getAccessToken();

  const res = await fetch(`${DASHBOARD_BASE}/schedule/sessions`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Create session failed", res.status);
  }

  return json;
}

export async function updateDashboardSession(
  session_id: number,
  payload: {
    dive_site_id?: number;
    dive_datetime?: string;
    meet_time?: string | null;
    notes?: string | null;
    session_type?: string;
    vessel_id?: number | null;
    lead_guide_id?: number | null;
    price_per_diver?: string | number | null;
  }
) {
  const token = await getAccessToken();

  const res = await fetch(`${DASHBOARD_BASE}/schedule/sessions/${session_id}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Update session failed", res.status);
  }

  return json;
}

export async function cancelDashboardSession(session_id: number) {
  const token = await getAccessToken();

  const res = await fetch(
    `${DASHBOARD_BASE}/schedule/sessions/${session_id}/cancel`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Cancel session failed", res.status);
  }

  return json;
}

export async function getDashboardSchedulePatterns() {
  const token = await getAccessToken();

  const res = await fetch(`${DASHBOARD_BASE}/schedule/patterns`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    cache: "no-store",
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Load schedule patterns failed", res.status);
  }

  return json;
}

export async function createDashboardSchedulePattern(payload: {
  weekday: number;
  start_time: string;
  meet_time?: string | null;
  dive_site_id: number;
  session_type: string;
  dive_category?: string | null;
  vessel_id?: number | null;
  lead_guide_id?: number | null;
  default_capacity?: number | null;
  price_per_diver?: string | number | null;
}) {
  const token = await getAccessToken();

  const res = await fetch(`${DASHBOARD_BASE}/schedule/patterns`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Create schedule pattern failed", res.status);
  }

  return json;
}

export async function updateDashboardSchedulePattern(patternId: number, payload: {
  weekday: number;
  start_time: string;
  meet_time?: string | null;
  dive_site_id: number;
  session_type: string;
  dive_category?: string | null;
  vessel_id?: number | null;
  lead_guide_id?: number | null;
  default_capacity?: number | null;
  price_per_diver?: string | number | null;
}) {
  const token = await getAccessToken();

  const res = await fetch(`${DASHBOARD_BASE}/schedule/patterns/${patternId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Create schedule pattern failed", res.status);
  }

  return json;
}

export async function deleteDashboardSchedulePattern(patternId: number) {
  const token = await getAccessToken();

  const res = await fetch(`${DASHBOARD_BASE}/schedule/patterns/${patternId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Create schedule pattern failed", res.status);
  }

  return json;
}

export async function generateDashboardScheduleFromPatterns(payload: {
  start_date: string;
  end_date: string;
}) {
  const token = await getAccessToken();

  const res = await fetch(`${DASHBOARD_BASE}/schedule/patterns/generate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Generate schedule from patterns failed", res.status);
  }

  return json;
}

export async function deleteDashboardSession(session_id: number) {
  const token = await getAccessToken();

  const res = await fetch(
    `${DASHBOARD_BASE}/schedule/sessions/${session_id}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Delete session failed", res.status);
  }

  return json;
}


export async function updateDashboardCourseRun(
  operatorId: number | string,
  courseRunId: number | string,
  payload: {
    start_at?: string;
    end_at?: string | null;
    max_capacity?: number;
    status?: string;
    location?: string | null;
    notes?: string | null;
    instructor_id?: number | null;
  }
) {
  const token = await getAccessToken();

  const res = await fetch(`${API_BASE_URL}/api/v3/operator/${operatorId}/courses/runs/${courseRunId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Update course run failed", res.status);
  }

  return json;
}

export async function cancelDashboardCourseRun(
  operatorId: number | string,
  courseRunId: number | string
) {
  return updateDashboardCourseRun(operatorId, courseRunId, { status: "cancelled" });
}

export async function getDashboardSchedulePatternLookups() {
  const token = await getAccessToken();

  const res = await fetch(`${DASHBOARD_BASE}/schedule/pattern-lookups`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    cache: "no-store",
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    throwApiError(json, "Load schedule pattern lookups failed", res.status);
  }

  return json;
}
